/**
 * The pane's one-line summary after a connection string was applied (Option C, C3; plan §3.2 "an optional paste or
 * prefill summary line") or a Browse Azure resource was picked (final UX review UX-01): which fields it filled, what it
 * left out, and the required fields still empty — read live, so the list shrinks as the user fills them. Labels only —
 * the string itself is never shown again, and a resource name goes through the redactor. Shown while the engine it
 * filled is on screen. After an Azure pick, a Microsoft Entra ID sign-in on screen says no password is needed — read live
 * too, so switching to a password sign-in drops the sentence (uxfix review UXF-02).
 */
import { CheckCircle2 } from 'lucide-react';
import { listLabels, type AppliedPaste } from './pastePreview';

type Props = {
  applied: AppliedPaste;
  stillNeeded: readonly string[];
  /** The form's sign-in now is Microsoft Entra ID. */
  entraSignIn: boolean;
};

function sourceOf(applied: AppliedPaste): string {
  if (applied.via === 'azure') return `Azure (${applied.resourceName ?? 'the picked resource'})`;
  return applied.via === 'pasted' ? 'the pasted connection string' : 'the connection string';
}

export function PasteAppliedLine({ applied, stillNeeded, entraSignIn }: Props) {
  const source = sourceOf(applied);
  return (
    <div
      className="mb-3 flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs leading-snug"
      role="status"
      data-testid={applied.via === 'azure' ? 'connection-azure-applied' : 'connection-string-applied'}
      data-engine={applied.engine}
    >
      <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
      <p className="min-w-0 break-words">
        {applied.labels.length > 0 ? `Filled ${listLabels(applied.labels)} from ${source}.` : `Nothing could be filled from ${source}.`}
        {applied.problem && <span className="text-warning"> It can’t be used as it is: {applied.problem}.</span>}
        {applied.unmapped.length > 0 && <span className="text-muted-foreground"> Not mapped, left out: {applied.unmapped.join(', ')}.</span>}
        {applied.tlsNote && <span className="text-muted-foreground"> {applied.tlsNote}</span>}
        {stillNeeded.length > 0 && <span> Still needed: {listLabels(stillNeeded)}.</span>}
        {applied.via === 'azure' && entraSignIn && <span className="text-muted-foreground"> No password needed: Microsoft Entra ID signs you in when you connect.</span>}
        {applied.entraHint && <span className="text-muted-foreground"> Azure SQL? Consider Microsoft Entra authentication.</span>}
      </p>
    </div>
  );
}
